import { cva, type VariantProps } from 'class-variance-authority'
import {
	Input as AriaInput,
	SearchField as AriaSearchField,
	type SearchFieldProps as AriaSearchFieldProps,
} from 'react-aria-components'
import { cn } from '../../lib/cn'
import { fieldFocusRing } from '../../lib/focus-ring'
import { Icon } from '../../atoms/icon/icon'

/**
 * The Search set (498:6573): Type = Default (445:47457), the grey 400x40 field of
 * the app header, and Type = Table (498:6591), the white field above a table.
 * Width is the caller's: the board draws 400px and 278px, the Work Orders list
 * (321:25513) stretches the Table one.
 *
 * Keyboard focus takes the kit's accent outline, which is not drawn in the Figma:
 * the field draws it while its input has keyboard focus, not on a click.
 */
export const searchVariants = cva(
	['flex h-10 min-w-0 items-center rounded-atlas-full px-4 py-1.5 font-atlas-sans', fieldFocusRing],
	{
		variants: {
			type: {
				default: 'gap-2.5 bg-atlas-background',
				table: 'gap-2 bg-atlas-light',
			},
		},
		defaultVariants: { type: 'default' },
	},
)

// The Default placeholder is drawn in Dark, the Table one in Light dark. Typed
// text is not drawn on either, so it takes Dark.
const searchInputVariants = cva(
	'min-w-0 flex-1 bg-transparent text-atlas-dark outline-none [&::-webkit-search-cancel-button]:appearance-none',
	{
		variants: {
			type: {
				default: 'text-atlas-body-medium placeholder:text-atlas-dark',
				table: 'text-atlas-search-table placeholder:text-atlas-light-dark',
			},
		},
		defaultVariants: { type: 'default' },
	},
)

export interface SearchProps
	extends Omit<AriaSearchFieldProps, 'className' | 'children' | 'type'>,
		VariantProps<typeof searchVariants> {
	className?: string
	/** Hint shown while the field is empty: "Search" on the header, "Search by number or ID" above a table. */
	placeholder?: string
}

/**
 * A rounded search field with a leading magnifier, built on react-aria's
 * SearchField: Enter submits (`onSubmit`) and Escape clears. The Figma draws no
 * visible label, so pass `aria-label`.
 */
export function Search({ type, placeholder = 'Search', className, ...props }: SearchProps) {
	return (
		<AriaSearchField {...props} className={cn(searchVariants({ type }), className)}>
			<Icon name={type === 'table' ? 'search-01' : 'search'} />
			<AriaInput placeholder={placeholder} className={searchInputVariants({ type })} />
		</AriaSearchField>
	)
}
